import { cn, InfoHint, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@eo/shared';

import { ROLE_DOT_CLASS, ROLE_RING_CLASS } from './constants';
import { formatHours, monthShare, ringArcs, ringTick } from './helpers';
import type { MemberCapacityProps } from './types';

// Committed client hours against the hours this person has for client work:
// net capacity times their client-service share of the month. The ring is the
// capacity; logged hours fill it solid and the rest of the committed hours
// faded, with any excess traced outside it.
export function MemberCapacity({ member, progress, isMe }: MemberCapacityProps) {
  const role = member.role === 'data_team' ? 'dataTeam' : 'cfo';
  const capacity = member.clientCapacity;
  const committed = member.assigned.total;
  const logged = member.categoryActuals.csd;
  const over = committed - capacity;
  const csdShare = member.split ? member.split.csd : 100;
  const offHours = member.capacity.timeOffHours + member.capacity.holidayHours;
  const pct = capacity > 0 ? Math.round((committed / capacity) * 100) : 0;
  const scale = Math.max(capacity, committed, logged);
  const arcs = ringArcs(capacity, logged, committed);
  const capacityTick = scale > capacity ? ringTick(capacity, scale) : null;
  // Pace follows what was committed, not the whole capacity: logging all of
  // the capacity is not the job, delivering the commitments is.
  const paceHours = committed * monthShare(progress);
  const pace = ringTick(paceHours, scale);

  // A tick on the ring that says what it marks when pointed at; a wide clear
  // stroke under the thin line makes it easy to point at.
  const tick = (
    testId: string,
    coords: ReturnType<typeof ringTick>,
    hint: string,
    line: { strokeWidth: string; strokeDasharray?: string; className: string },
  ) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <g data-testid={testId} tabIndex={-1} className="cursor-help">
          <line {...coords} strokeWidth="8" className="stroke-transparent" />
          <line {...coords} {...line} />
        </g>
      </TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  );

  const lines = [
    // The portfolio figures beside this count everyone, so this one says whose.
    {
      label: isMe ? 'You logged' : `${member.name} logged`,
      hours: logged,
      dot: ROLE_DOT_CLASS[role],
    },
    {
      label: 'Committed, not logged yet',
      hours: Math.max(0, committed - logged),
      dot: ROLE_RING_CLASS[role].faded,
    },
    over > 0
      ? { label: 'Over capacity', hours: over, dot: 'bg-destructive' }
      : { label: 'Open capacity', hours: -over, dot: 'bg-capacity-rail' },
  ];

  return (
    <TooltipProvider delayDuration={100}>
      <div data-testid="member-capacity" className="flex flex-wrap items-center gap-8">
        <div className="relative size-44 shrink-0">
          <svg
            viewBox="0 0 120 120"
            role="img"
            aria-label={`${pct}% of the hours for client work committed`}
            className="size-full -rotate-90"
          >
            <circle
              cx="60"
              cy="60"
              r="50"
              pathLength="100"
              fill="none"
              strokeWidth="10"
              className="stroke-capacity-rail"
            />
            {arcs.map((arc) => (
              <circle
                key={arc.key}
                cx="60"
                cy="60"
                r={arc.key === 'over' ? 57 : 50}
                pathLength="100"
                fill="none"
                strokeWidth={arc.key === 'over' ? 2 : 10}
                strokeDasharray={`${arc.length} ${100 - arc.length}`}
                strokeDashoffset={-arc.offset}
                className={
                  arc.key === 'over' ? 'stroke-destructive' : ROLE_RING_CLASS[role][arc.key]
                }
              />
            ))}
            {capacityTick &&
              tick(
                'ring-capacity',
                capacityTick,
                `Capacity for client work, ${formatHours(capacity)}: committed hours past this line are over what you have for client work this month.`,
                { strokeWidth: '1.5', className: 'stroke-text-primary' },
              )}
            {paceHours > 0 &&
              tick(
                'ring-pace',
                pace,
                `Where logged hours should be today, ${formatHours(paceHours)}: your committed hours times the share of the month’s working days already gone.`,
                { strokeWidth: '1', strokeDasharray: '1.5 1.5', className: 'stroke-text-tertiary' },
              )}
          </svg>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
            <span
              className={cn(
                'text-2xl leading-[26px] tabular-nums',
                over > 0 ? 'text-destructive' : 'text-text-secondary',
              )}
            >
              {pct}%
            </span>
            <span className="text-xs text-text-tertiary">committed</span>
            <span className="pointer-events-auto mt-1 flex items-center gap-1 text-xs tabular-nums text-text-tertiary">
              {formatHours(committed)} / {formatHours(capacity)}
              <InfoHint term="capacity for client work">
                {`${formatHours(capacity)}: ${member.capacity.workingDays} working days − ${formatHours(offHours)} off & holidays, times the ${csdShare}% share of client work.`}
              </InfoHint>
            </span>
          </div>
        </div>
        <div className="flex min-w-56 flex-1 flex-col gap-3">
          <ul className="flex flex-col gap-2 text-sm">
            {lines.map((line) => (
              <li key={line.label} className="flex items-center justify-between gap-6">
                <span className="flex items-center gap-2 text-text-tertiary">
                  <span className={cn('size-2.5 rounded-full', line.dot)} />
                  {line.label}
                </span>
                <span
                  className={cn(
                    'tabular-nums',
                    line.label === 'Over capacity' ? 'text-destructive' : 'text-text-secondary',
                  )}
                >
                  {formatHours(line.hours)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </TooltipProvider>
  );
}
